import { action, computed, makeObservable, observable } from 'mobx'
import { combineLatest, Subscription } from 'rxjs'
import { SearchableUser } from 'iam/models/SearchableUser'
import { UserDataType } from 'iam/models/UserDataType'
import Workspace from 'iam/models/Workspace'
import RoleStore from 'iam/RoleStore'
import SearchableUserStore from 'iam/SearchableUserStore'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import {
  FilterItem,
  FilterItemSource,
  FilterStorageFactory,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { switchMap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { tapAction } from 'utils/mobx-util'

type UserFilter = {
  workspace?: Workspace | null
  userTypes?: UserDataType[] | null
  inactive?: boolean | null
  role?: Id | null
}

type UserSearchFilter = UserFilter & { searchText?: string }

const UserTypeOptions = [
  {
    title: 'Admin',
    value: UserDataType.Admin,
  },
  {
    title: 'User',
    value: UserDataType.User,
  },
  {
    title: 'Pro',
    value: UserDataType.Pro,
  },
]

export class UserSearchViewModel {
  private subscription = new Subscription()

  searchText = new SearchTextViewModel(500)

  filterStorage = this.createFilterStorage('UserManagementTableFilters')

  @observable
  totalUsers = 0

  filters = new FiltersViewModel<UserFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspace: {
        title: 'Workspace',
        implicit: true,
        persist: false,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            Workspace.none,
            FilterItemSource.static([]),
          ),
      },
      userTypes: {
        icon: 'users',
        title: 'User types',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(UserTypeOptions),
            true,
          ),
      },
      inactive: {
        icon: 'pending',
        title: 'Inactive',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            false,
            FilterItemSource.static([]),
          ),
      },
      role: {
        icon: 'roles',
        title: 'Role',
        persist: false,
        isVisible: (): boolean => this.shouldShowRolesFilter,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            undefined,
            async () => this._fetchRoles(),
            false,
          ),
      },
    },
  })

  scrollProvider = new InfiniteScrollProvider<SearchableUser, UserSearchFilter>(
    {
      limit: 100,
      startFetchTaskAsPending: true,
      fetchItems: (paging, _, filter) =>
        this.store.fetchUsers({
          search_text: filter?.searchText,
          limit: paging.limit,
          after: paging.after,
          workspace_id: filter?.workspace?.id,
          user_types: filter?.userTypes,
          inactive: filter?.inactive,
          role_id: filter?.role || undefined,
        }),
    },
  )

  private query$ = combineLatest([
    this.filters.appliedFilters$,
    this.searchText.debouncedValue$,
  ]).pipe(
    switchMap(([appliedFilters, searchText]) =>
      this.scrollProvider.fetch({ searchText, ...appliedFilters }),
    ),
    tapAction((result) => (this.totalUsers = result!.total!)),
  )

  constructor(
    private store: SearchableUserStore,
    private roleStore: RoleStore,
    private createFilterStorage: FilterStorageFactory,
    private setDefaultWorkspace: () => void,
  ) {
    makeObservable(this)
  }

  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace
    >
  }

  @computed
  get loadedUsersCount() {
    return this.scrollProvider.items.length
  }

  @computed
  get userInactiveFilter() {
    return this.filters.getFilter('inactive') as SingleSelectFilter<
      string,
      boolean
    >
  }

  activate() {
    this.filters.activate()
    this.setDefaultWorkspace()
    this.subscription = this.query$.subscribe()
  }

  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }

  @action
  removeUsers(userIds: Array<Id>) {
    userIds.forEach((userId) => {
      const index = this.scrollProvider.items.findIndex(
        (user) => user.publicId === userId,
      )

      this.scrollProvider.items.splice(index, 1)
    })
  }

  @computed
  get shouldShowRolesFilter() {
    return Boolean(this.workspaceFilter.value.id)
  }

  clearWorkspaceScopedFilters() {
    this.filters.clearFilter('role')
  }

  private async _fetchRoles(): Promise<FilterItem<Id>[]> {
    if (!this.workspaceFilter.value) {
      return []
    }

    const roles = await this.roleStore.find({
      workspace_id: Id.assert(this.workspaceFilter.value.id),
    })

    return roles.map((r) => {
      return { title: r.name, description: r.description, value: r.id }
    })
  }
}
